import { Fragment, ReactNode, useCallback } from 'react';

import {
  ActivitiesComponentType,
  ACTIVITIES_COMPONENT_TYPE_COLUMNS,
} from 'global/constants';
import { ESCAPE } from 'approot/shared/ally/keyboard.hooks';
import { useContextualSurface } from 'approot/shared/ally/contextual.hooks';
import ActivityBuilderInlineSvg from 'approot/shared/inline-svg/activity-builder/activity-builder-inline-svg';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import Settings from './settings';
import {
  ComponentAddMenuIconButton,
  ComponentsAddMenu,
} from '../components/components-menu/components-add-menu';
import { AddComponentTemplate } from '../components/components-menu/components-menu.constants';
import { ColumnAddMenu } from '../components/columns/column-add-menu';
import {
  ACTIVITY_BUILDER_COMPONENT_MENU,
  ACTIVITY_BUILDER_COMPONENT_MENU_EXTRA,
} from '../activity-builder.constants';
import { useActivityBuilderContext } from '../activity-builder.context';
import * as Styled from './settings-component-menu.style';
import { formatToLowerKebabCase } from 'approot/shared/logic/format-to-lower-kebab-case';
import ComponentSettingsBorders from '../components/shared/component-settings-borders';
import { ComponentOuterStyle } from '../components/shared/component-settings-borders.constants';

type baseProps = {
  addComponent?: (props: AddComponentTemplate) => void;
};

type ActionMenuOption = {
  onClick: () => void;
  ariaLabel: string;
  icon: ReactNode;
  disabled?: boolean;
  divider?: boolean;
  tooltip?: string;
};

type Props = {
  options: ActionMenuOption[];
  componentSettings?: ReactNode;
  columnSettings?: ReactNode;
  componentId: string;
  columnComponentId?: string;
  type: ActivitiesComponentType;
  hasImage?: boolean;
  customSettingsMenu?: ReactNode;
  sectionId?: string;
  style?: ComponentOuterStyle;
} & baseProps;

export const SETTINGS_MENU_ID = 'lb-inline-settings';

export const SettingsComponentMenu = ({
  options,
  componentSettings,
  componentId,
  columnComponentId,
  columnSettings,
  type,
  hasImage,
  customSettingsMenu,
  sectionId,
  style,
  addComponent,
}: Props) => {
  const {
    activeComponent,
    setActiveComponent,
    setCroppingComponentId,
    croppingComponentId,
  } = useActivityBuilderContext();

  const { styles, attributes, setReferenceElement, setPopperElement } =
    useContextualSurface(true, () => {}, {
      offset: [0, 0],
      placement: 'top-end',
      fallbackPlacements: ['top-start'],
    });

  const hasActionMenu = !!(componentSettings || options.length > 0);
  const isColumn = !!columnSettings;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === ESCAPE) {
        e.stopPropagation();
        e.preventDefault();
        setActiveComponent(undefined);
      }
    },
    [setActiveComponent]
  );

  return (
    <Styled.SettingComponentMenuWrapper
      ref={setReferenceElement}
      id={SETTINGS_MENU_ID}
    >
      <Styled.SettingsComponentMenu
        data-selector="settings-component-menu"
        $hide={!!croppingComponentId}
        ref={setPopperElement}
        role="dialog"
        tabIndex={-1}
        style={styles.popper}
        {...attributes.popper}
        onKeyDown={handleKeyDown}
      >
        <div id={ACTIVITY_BUILDER_COMPONENT_MENU_EXTRA} />
        <Styled.SettingsComponentMenuInner id={ACTIVITY_BUILDER_COMPONENT_MENU}>
          {isColumn && addComponent ? (
            <ColumnAddMenu
              canRenderMenu={activeComponent?.id === componentId}
              addComponentToColumn={addComponent}
              render={onClick => (
                <ComponentAddMenuIconButton onClick={onClick} />
              )}
            />
          ) : (
            addComponent && (
              <ComponentsAddMenu
                canRenderMenu={activeComponent?.id === componentId}
                addComponent={addComponent}
                render={onClick => (
                  <ComponentAddMenuIconButton onClick={onClick} />
                )}
              />
            )
          )}

          <Styled.SettingsComponentMenuHr />

          {type !== ACTIVITIES_COMPONENT_TYPE_COLUMNS && sectionId && (
            <>
              <ComponentSettingsBorders
                componentId={componentId}
                sectionId={sectionId}
                style={style}
                type={type}
              />
              <Styled.SettingsComponentMenuHr />
            </>
          )}

          {hasActionMenu && (
            <>
              {componentSettings && (
                <Settings
                  type={type}
                  componentId={componentId}
                  columnComponentId={columnComponentId}
                  componentSettings={componentSettings}
                  columnSettings={columnSettings}
                />
              )}

              {customSettingsMenu && customSettingsMenu}

              {hasImage && (
                <TooltipWrapper hoverText="Reposition image">
                  <Styled.SettingsComponentMenuButton
                    type="button"
                    onClick={() => setCroppingComponentId(componentId)}
                    data-selector="icon-crop"
                  >
                    <ActivityBuilderInlineSvg
                      id="icon_crop"
                      width={20}
                      height={20}
                    />
                  </Styled.SettingsComponentMenuButton>
                </TooltipWrapper>
              )}

              {options.length > 0 && <Styled.SettingsComponentMenuHr />}

              {options.map((option, index) => (
                <Fragment key={index}>
                  {option.tooltip ? (
                    <TooltipWrapper hoverText={option.tooltip}>
                      <Styled.SettingsComponentMenuButton
                        type="button"
                        aria-label={option.ariaLabel}
                        disabled={option.disabled}
                        onClick={option.onClick}
                        data-selector={`settings-menu-${formatToLowerKebabCase(
                          option.tooltip
                        )}`}
                      >
                        {option.icon}
                      </Styled.SettingsComponentMenuButton>
                    </TooltipWrapper>
                  ) : (
                    <Styled.SettingsComponentMenuButton
                      type="button"
                      aria-label={option.ariaLabel}
                      disabled={option.disabled}
                      onClick={option.onClick}
                    >
                      {option.icon}
                    </Styled.SettingsComponentMenuButton>
                  )}

                  {option.divider && <Styled.SettingsComponentMenuHr />}
                </Fragment>
              ))}
            </>
          )}
        </Styled.SettingsComponentMenuInner>
      </Styled.SettingsComponentMenu>
    </Styled.SettingComponentMenuWrapper>
  );
};
